import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import TopBar from '../components/TopBar';
import { api } from '../api/client';

type ObraDetalle = {
  id: number;
  titulo: string;
  descripcion: string | null;
  ubicacion: string | null;
  area: string | null;
  estado: string;
  avance: number;
  fecha_inicio: string | null;
  fecha_fin_est: string | null;
  actualizado_en: string | null;
  latitud: number | null;
  longitud: number | null;
  zona_geojson: string | null;
  imagenes: { id: number; ruta: string }[];
};

const ESTADO_LABEL: Record<string, string> = {
  en_ejecucion: 'En ejecución',
  planificada:  'Planificada',
  suspendida:   'Suspendida',
  finalizada:   'Finalizada',
};
const ESTADO_COLOR: Record<string, string> = {
  en_ejecucion: '#10b981',
  planificada:  '#3b82f6',
  suspendida:   '#f59e0b',
  finalizada:   '#6b7280',
};

function fmtFecha(s: string | null) {
  if (!s) return '–';
  return new Date(s.replace(' ', 'T')).toLocaleDateString('es-AR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function makeIcon() {
  return L.divIcon({
    html: `<div style="background:#32329f;width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>`,
    className: '',
    iconSize: [26, 26],
    iconAnchor: [13, 26],
  });
}

function addZona(map: L.Map, raw: string): void {
  try {
    const gj = JSON.parse(raw);
    const style = { color: '#4080ff', weight: 2, fillOpacity: 0.15, fillColor: '#4080ff' };
    if (gj.type === 'Circle') {
      const { lat, lng } = gj.center;
      const circle = L.circle([lat, lng], { radius: gj.radius, ...style }).addTo(map);
      map.fitBounds(circle.getBounds(), { padding: [20, 20] });
    } else {
      const layer = L.geoJSON(gj, { style: () => style }).addTo(map);
      map.fitBounds(layer.getBounds(), { padding: [20, 20] });
    }
  } catch { /* zona inválida, ignorar */ }
}

export default function DetalleObra() {
  const { id } = useParams();
  const [obra, setObra]               = useState<ObraDetalle | null>(null);
  const [err, setErr]                 = useState<string | null>(null);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [mapaAbierto, setMapaAbierto] = useState(false);

  // Mapa preview (estático)
  const previewRef  = useRef<HTMLDivElement>(null);
  const previewMap  = useRef<L.Map | null>(null);

  // Mapa fullscreen (interactivo)
  const fullRef     = useRef<HTMLDivElement>(null);
  const fullMap     = useRef<L.Map | null>(null);

  useEffect(() => {
    api<ObraDetalle>(`/obras?id=${id}`)
      .then(setObra)
      .catch((e) => setErr(e.message));
  }, [id]);

  // Mapa preview — estático, solo muestra pin y área
  useEffect(() => {
    const tienePin  = !!(obra?.latitud && obra?.longitud);
    const tieneZona = !!(obra?.zona_geojson);
    if (!tienePin && !tieneZona) return;
    if (!previewRef.current || previewMap.current) return;

    const map = L.map(previewRef.current, {
      center: [obra?.latitud ?? 0, obra?.longitud ?? 0],
      zoom: 16,
      zoomControl: false,
      dragging: false,
      scrollWheelZoom: false,
      attributionControl: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);

    if (obra?.zona_geojson) addZona(map, obra.zona_geojson);
    if (tienePin) L.marker([obra!.latitud!, obra!.longitud!], { icon: makeIcon() }).addTo(map);
    if (!obra?.zona_geojson) map.setView([obra!.latitud!, obra!.longitud!], 16);

    previewMap.current = map;
    return () => { map.remove(); previewMap.current = null; };
  }, [obra]);

  // Mapa fullscreen — interactivo
  useEffect(() => {
    if (!mapaAbierto) {
      fullMap.current?.remove();
      fullMap.current = null;
      return;
    }
    // Bloquear scroll del body mientras el mapa está abierto
    document.body.style.overflow = 'hidden';

    const t = setTimeout(() => {
      if (!fullRef.current || fullMap.current || !obra) return;

      const tienePin = !!(obra.latitud && obra.longitud);
      const lat0 = obra.latitud  ?? 0;
      const lon0 = obra.longitud ?? 0;

      const map = L.map(fullRef.current, {
        center: [lat0, lon0],
        zoom: 16,
        zoomControl: false,       // sin botones +/- (usar pinch/doble-tap)
        attributionControl: false, // sin footer de OSM
        dragging: true,
        scrollWheelZoom: true,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);

      if (obra.zona_geojson) addZona(map, obra.zona_geojson);
      if (tienePin) {
        L.marker([lat0, lon0], { icon: makeIcon() }).addTo(map);
        if (!obra.zona_geojson) map.setView([lat0, lon0], 16);
      }

      fullMap.current = map;
    }, 60); // esperar que el div esté en el DOM

    return () => {
      clearTimeout(t);
      document.body.style.overflow = '';
    };
  }, [mapaAbierto, obra]);

  // Lightbox swipe
  const touchStart = useRef<number | null>(null);
  function onTouchStart(e: React.TouchEvent) { touchStart.current = e.touches[0].clientX; }
  function onTouchEnd(e: React.TouchEvent) {
    if (!obra || touchStart.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (dx > 50) setLightboxIdx((p) => Math.max(0, (p ?? 0) - 1));
    if (dx < -50) setLightboxIdx((p) => Math.min(obra.imagenes.length - 1, (p ?? 0) + 1));
  }

  if (err) return (
    <>
      <TopBar title="Obra" back />
      <main className="px-4 py-4">
        <div className="bg-red-50 border border-app text-red-700 text-sm rounded-xl p-3">{err}</div>
      </main>
    </>
  );
  if (!obra) return (
    <>
      <TopBar title="Obra" back />
      <main className="px-4 pt-10">
        <p className="text-center text-gray-400">Cargando…</p>
      </main>
    </>
  );

  const color     = ESTADO_COLOR[obra.estado] ?? '#6b7280';
  const barPct    = Math.min(100, Math.max(0, obra.avance));
  const tieneUbic = !!(obra.latitud && obra.longitud) || !!(obra.zona_geojson);

  return (
    <>
      <TopBar title="Detalle de obra" back />

      {/* ── Mapa fullscreen ──────────────────────────────────────────── */}
      {mapaAbierto && (
        <div className="fixed inset-0 flex flex-col" style={{ zIndex: 9000, backgroundColor: '#000' }}>

          {/* Barra superior */}
          <header
            className="flex items-center gap-3 px-4 flex-shrink-0"
            style={{
              backgroundColor: '#dedede',
              borderBottom: '1px solid #c8c8c8',
              paddingTop: 'calc(env(safe-area-inset-top) + 12px)',
              paddingBottom: '12px',
            }}
          >
            <button
              onClick={() => setMapaAbierto(false)}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-xl"
              style={{ backgroundColor: 'rgba(100,116,139,0.13)', border: '1px solid rgba(100,116,139,0.22)', color: '#64748b' }}
              aria-label="Cerrar mapa"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                   strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <h1 className="flex-1 font-bold text-lg truncate" style={{ color: '#303030' }}>
              {obra.titulo}
            </h1>
          </header>

          {/* Mapa — ocupa todo el espacio restante */}
          <div ref={fullRef} style={{ flex: 1 }} />
        </div>
      )}

      {/* ── Lightbox ─────────────────────────────────────────────────── */}
      {lightboxIdx !== null && obra.imagenes.length > 0 && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.95)' }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onClick={() => setLightboxIdx(null)}
        >
          <img
            src={`/ciudadapp/uploads/obras/${obra.imagenes[lightboxIdx].ruta}`}
            alt=""
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
          />
          {obra.imagenes.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5">
              {obra.imagenes.map((_, i) => (
                <div key={i} className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: i === lightboxIdx ? '#fff' : 'rgba(255,255,255,.35)' }} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Contenido ────────────────────────────────────────────────── */}
      <main className="max-w-md mx-auto px-4 py-4 pb-24 space-y-4">

        <div>
          <span className="text-xs font-bold px-2.5 py-0.5 rounded-full"
            style={{ backgroundColor: color + '20', color }}>
            {ESTADO_LABEL[obra.estado] ?? obra.estado}
          </span>
          <h1 className="text-xl font-extrabold text-gray-900 mt-2 leading-snug">{obra.titulo}</h1>
          {obra.ubicacion && <p className="text-sm text-gray-500 mt-0.5">📍 {obra.ubicacion}</p>}
          {obra.area && <p className="text-xs text-gray-400 mt-0.5">{obra.area}</p>}
        </div>

        <div className="bg-white border border-app rounded-xl p-4 space-y-3">
          <div>
            <div className="flex justify-between text-sm mb-1.5">
              <span className="font-semibold text-gray-700">Avance</span>
              <span className="font-extrabold text-gray-900">{barPct}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
              <div className="h-3 rounded-full transition-all"
                style={{ width: `${barPct}%`, backgroundColor: color }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div>
              <p className="text-xs text-gray-400">Inicio</p>
              <p className="text-sm font-semibold text-gray-800">{fmtFecha(obra.fecha_inicio)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Fin estimado</p>
              <p className="text-sm font-semibold text-gray-800">{fmtFecha(obra.fecha_fin_est)}</p>
            </div>
          </div>
          {obra.actualizado_en && (
            <p className="text-xs text-gray-300 pt-1">Actualizado: {fmtFecha(obra.actualizado_en)}</p>
          )}
        </div>

        {obra.descripcion && (
          <div className="bg-white border border-app rounded-xl p-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Descripción</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{obra.descripcion}</p>
          </div>
        )}

        {obra.imagenes.length > 0 && (
          <div className="bg-white border border-app rounded-xl p-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">📷 Fotos</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {obra.imagenes.map((img, i) => (
                <button key={img.id} onClick={() => setLightboxIdx(i)}
                  className="flex-shrink-0 active:scale-95 transition-transform">
                  <img
                    src={`/ciudadapp/uploads/obras/${img.ruta}`}
                    alt=""
                    className="w-32 h-24 rounded-xl object-cover border border-app bg-gray-100"
                    onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Mapa preview + botón para abrir fullscreen */}
        {tieneUbic && (
          <div className="bg-white border border-app rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
              📍 {obra.zona_geojson ? 'Área afectada' : 'Ubicación en el mapa'}
            </p>

            {/* Preview estático — toca para abrir fullscreen */}
            <button
              onClick={() => setMapaAbierto(true)}
              className="w-full block active:opacity-80 transition-opacity"
              style={{ borderRadius: '0.75rem', overflow: 'hidden', border: '1px solid #6e6b6b' }}
              aria-label="Abrir el mapa"
            >
              <div ref={previewRef} style={{ height: 200, pointerEvents: 'none' }} />
            </button>

            <button
              onClick={() => setMapaAbierto(true)}
              className="w-full text-center border border-app rounded-xl py-2.5 text-sm text-gray-600 font-medium active:bg-gray-50"
            >
              🗺️ Abrir el mapa
            </button>
          </div>
        )}
      </main>
    </>
  );
}
